import { useCallback } from "react";
import type { Action } from "../actions.types";
import type {
  ActionRegistry,
  ActionHelpers,
  ActionStatusType,
  GenericDisplayOptions,
} from "../actions.types";
import { ACTION_STATUS } from "../actions.constants";
import {
  getConfigForAction,
  isActionCompleted,
  isActionAvailable,
} from "../utils";

export type PhaseActionResult = {
  /** CTA button label (from display_options.cta.labels[status] or action.name) */
  label: string;
  /** Status label (e.g., "Completed", "Due Jan 5th") */
  statusLabel: string | null;
  /** Click handler */
  onClick: () => void;
  /** Whether action is completed */
  isCompleted: boolean;
  /** Whether action is available (not past closed_date) */
  isAvailable: boolean;
  /** CTA URL from display_options (relative to /program/{programId}) */
  ctaUrl: string | undefined;
  /** Whether CTA URL is external (opens in new tab) */
  ctaIsExternal: boolean;
};

/**
 * Get display options from action with type safety.
 * Reads from the new cta structure with status-specific labels.
 * For "Action Issued" status, falls back to "Not Started" label if not explicitly set.
 */
function getDisplayOptions(action: Action) {
  const opts = action.display_options as GenericDisplayOptions | null;
  const statusName = (action.current_status?.name ??
    ACTION_STATUS.NOT_STARTED) as ActionStatusType;

  // For ACTION_ISSUED, fallback to NOT_STARTED label if not explicitly set
  const fallbackKey =
    statusName === ACTION_STATUS.ACTION_ISSUED
      ? ACTION_STATUS.NOT_STARTED
      : statusName;

  return {
    ctaLabel:
      opts?.cta?.labels?.[statusName] ??
      opts?.cta?.labels?.[fallbackKey] ??
      action.name ??
      "",
    ctaUrl: opts?.cta?.url,
    ctaIsExternal: opts?.cta?.isExternal ?? false,
  };
}

/**
 * Format due date for display.
 */
function formatDueDate(
  dueDate: string | null,
  timezone: string
): string | null {
  if (!dueDate) return null;
  try {
    const date = new Date(dueDate);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: timezone,
    });
  } catch {
    return null;
  }
}

/**
 * Get status label: "Due {date}" for incomplete actions with due dates.
 * Returns null for completed actions (shown via checkmark icon instead).
 */
function getStatusLabel(action: Action, timezone: string): string | null {
  if (isActionCompleted(action)) return null;
  const dueDate = formatDueDate(action.due_date, timezone);
  return dueDate ? `Due ${dueDate}` : null;
}

/**
 * Get the onClick handler and display properties for a phase action.
 *
 * @param registry - The action registry
 * @param action - The action to get handler for
 * @param helpers - Helper functions (navigate, openDialog, openSheet, updateStatus)
 * @param timezone - User's timezone (IANA format, e.g., "America/New_York")
 * @returns Object with onClick handler and display properties
 */
export function usePhaseAction(
  registry: ActionRegistry,
  action: Action,
  helpers: ActionHelpers,
  timezone: string = "UTC"
): PhaseActionResult {
  const config = getConfigForAction(registry, action);

  const onClick = useCallback(() => {
    if (config) {
      config.onClick(action, helpers);
    }
  }, [config, action, helpers]);

  const displayOpts = getDisplayOptions(action);

  return {
    label: displayOpts.ctaLabel,
    statusLabel: getStatusLabel(action, timezone),
    onClick,
    isCompleted: isActionCompleted(action),
    isAvailable: isActionAvailable(action),
    ctaUrl: displayOpts.ctaUrl,
    ctaIsExternal: displayOpts.ctaIsExternal,
  };
}
